import { I18n } from "@assets/strings";
import React, { useState } from "react";
import { ClipLoader } from "react-spinners";
import styled, { css } from "styled-components";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { allWalletType, Group, WalletType } from "@domain/group/group";
import { useAsync } from "@utils/hooks";
import { ClosableModal } from "@ui/modals/closableModal";
import { useModalTools } from "@ui/modals/modalContext";
import { CashoutTreatment } from "@ui/pages/group/cashoutTreatment";
import { gap } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { Selecter } from "@ui/components/inputs/selecter";
import { stack } from "@ui/components/layout";
interface ShareroomCashoutProps extends React.HTMLAttributes<HTMLDivElement> {
	close: () => void;
	cashableIUC?: number;
	shareroom: Group;
	month: string | null;
}

export const ShareroomCashout: React.FC<ShareroomCashoutProps> = ({
	close,
	cashableIUC,
	month,
	shareroom,
	...htmlProps
}) => {
	const [wallet, setWallet] = useState<WalletType | undefined>(allWalletType[0]);
	const currentProfile = useCurrentProfile();
	const { walletService } = useServices();
	const { createModal } = useModalTools();

	const { run, loading } = useAsync(async () => {
		try {
			await walletService.cashout({ serviceCode: wallet, month: month ?? undefined }, shareroom.id);
			close();
			createModal((close) => <CashoutTreatment shareroom={shareroom} close={close} />);
		} catch (e) {
			console.error(e);
			createToast(I18n.t("error"), I18n.t("auth.error.default"), "error");
		}
	}, [wallet]);
	return (
		<Container close={close} {...htmlProps}>
			<ContainerLabel>
				<Info>
					{I18n.t("shareroom.operation.cashout.description_1")}
					<b style={{ color: "#206AF8", fontSize: "16px" }}>
						{cashableIUC} {currentProfile?.currency}
					</b>{" "}
					{I18n.t("shareroom.operation.cashout.description_2")}
				</Info>
			</ContainerLabel>
			<InfoEdition>
				{/* TODO add wording for this */}
				<PrimaryMedium>Mobile money </PrimaryMedium>
				<Selecter
					value={wallet}
					onChange={(value) => setWallet(value as WalletType)}
					options={allWalletType.map((val) => ({
						value: val,
						label: I18n.t(`shareroom.operation.cashout.mode.${val}`),
					}))}
					placeholder="Choisir un moyen de paiement"
				/>
			</InfoEdition>

			<InfoEdition>
				<PrimaryMedium>{I18n.t("shareroom.subscription.phone.title")} </PrimaryMedium>
				<TextInput value={currentProfile?.phoneNumber} disabled />
			</InfoEdition>
			{loading ? (
				<ClipLoader size={60} />
			) : (
				<BlocButton onClick={run}>
					<StyledButtonContainer>{I18n.t("confirm")}</StyledButtonContainer>
				</BlocButton>
			)}
		</Container>
	);
};

const Container = styled(ClosableModal)`
	${gap(20)};
	display: flex;
	justify-content: center;
	align-items: center;
`;
const ContainerLabel = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
`;

const Info = styled.div`
	${({ theme }) => theme.textStyles.description};
	font-style: normal;
	font-weight: 400;
	font-size: 15px;
	line-height: 30px;
	text-align: center;
	color: #000000;
	margin-top: 30px;
	margin-bottom: 14px;
	@media (max-width: 900px) {
		font-size: 13px;
	}
`;

const InfoEdition = styled.div`
	${stack(10, "flex-start", "stretch")};
	gap: 12px;
	width: 80%;
`;

const TextInput = styled.input<{ isInvalid?: boolean }>`
	${({ theme }) => theme.containerStyles.roundedBlockStyle};
	padding: 10px 12px 9px;
	border-radius: 10px;
	background: #cfcfcf;
	border: 1px solid rgba(197, 194, 194, 0.4);
	font-size: 0.935rem;
	outline: none;

	:disabled {
		color: ${({ theme }) => theme.colors.textTertiary};
	}

	:focus {
		border: 1px solid ${({ theme, isInvalid }) => (isInvalid ? theme.colors.red : theme.colors.secondary)};
	}

	${({ isInvalid }) =>
		isInvalid &&
		css`
			border: 1px solid ${({ theme }) => theme.colors.red};
		`}

	@media (max-width: 370px) {
		width: 140px;
	}
`;

const BlocButton = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	margin-top: 30px;
`;
const StyledButtonContainer = styled(PrimaryButton)``;
